import { map, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';

import { Observable, Subject, Subscription } from 'rxjs';

import { DocumentType } from '../../../models/document-type';

import { DocumentTypeService } from '../../../services/documents/document-type.service';
import { NotyService } from '../../../services/common/noty.service';

declare var swal: any;

@Component({
  selector: 'app-document-type-index',
  templateUrl: './document-type-index.component.html',
  styleUrls: ['./document-type-index.component.scss'],
})
export class DocumentTypeIndexComponent implements OnInit, OnDestroy {
  deleting: boolean = false;

  documentTypes: Array<DocumentType> = [];

  formOptions: any = {
    prefixes: [],
  };

  loading: boolean = true;

  params: any = {};

  rightMarginTop: number = 0;

  saving: boolean = false;

  searchTermChanged: Subject<string> = new Subject<string>();

  searchSubscription: Subscription;

  selectedDocumentType: DocumentType | null;

  editDocumentType: DocumentType | null;

  addChildDocumentType: DocumentType | null;

  newDocumentType: DocumentType | null;

  updatedDocumentTypeNumber: number = 0;

  newParentId: number;

  constructor(private documentTypeService: DocumentTypeService, private notyService: NotyService) {
    this.loadDocumentTypes(true);
  }

  // Component lifecycle methods
  ngOnInit() {
    this.searchSubscription = this.searchTermChanged
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((search) => this.loadDocumentTypes() || new Observable())
      )
      .subscribe(() => {});
  }

  ngOnDestroy() {
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
  }

  // Event Listeners
  @HostListener('window:scroll', ['$event'])
  window_scroll(event: Event) {
    this.setRightMarginTop();
  }

  @HostListener('window:resize', ['$event'])
  window_resize(event: Event) {
    this.setRightMarginTop();
  }

  // Component methods
  addChildDocumentTypeSet() {
    this.addChildDocumentType = new DocumentType();
    this.addChildDocumentType.parent_id = this.selectedDocumentType!.id;
    this.editDocumentType = null;
    this.newDocumentType = null;
  }

  deleteDocumentType(documentType: DocumentType) {
    let scope: DocumentTypeIndexComponent = this;
    swal({
      title: 'Delete DocumentType',
      text: `Delete ${documentType.prefix} - ${documentType.name} ?`,
      icon: 'warning',
      dangerMode: true,
      buttons: {
        cancel: {
          text: 'No',
          value: false,
          visible: true,
          className: '',
          closeModal: true,
        },
        confirm: {
          text: 'Yes',
          value: true,
          visible: true,
          className: '',
          closeModal: true,
        },
      },
    })
      .then((confirmed: any) => {
        if (confirmed) {
          this.deleting = true;
          this.documentTypeService
            .delete(documentType.id)
            .then((response_json) => {
              this.deleting = false;
              if (response_json.success) {
                this.notyService.notify(response_json.text || `Deleted document type ${documentType.prefix} - ${documentType.name}`, 'warning');
                if (!response_json.parent_id) {
                  this.loadDocumentTypes();
                  this.selectedDocumentType = null;
                } else {
                  this.newParentId = response_json.parent_id;
                  this.selectedDocumentType = null;
                  this.reloadDocumentType(documentType);
                }
              } else {
                this.notyService.notify(response_json.text || 'Error deleting document type', 'error');
              }
            })
            .catch(function (err) {
              console.log(err);
              scope.deleting = false;
              scope.notyService.notify('Error deleting document type', 'error');
            });
        }
      })
      .catch((err: any) => {
        console.log('Delete prompt error', err);
      });
  }

  editDocumentTypeSet() {
    this.editDocumentType = this.selectedDocumentType;
    this.addChildDocumentType = null;
    this.newDocumentType = null;
  }

  loadDocumentTypes(promise: boolean = false): Observable<any> | void {
    if (promise)
      this.documentTypeService.index(this.params).then((response_json) => {
        this.loading = false;
        for (let i = 0; i < 26; i += 1) this.formOptions.prefixes.push(String.fromCharCode(65 + i));
        this.documentTypes = response_json.document_types;
      });
    else
      return this.documentTypeService.indexObservable(this.params).pipe(
        map((response_json) => {
          this.loading = false;
          for (let i = 0; i < 26; i += 1) this.formOptions.prefixes.push(String.fromCharCode(65 + i));
          this.documentTypes = response_json.document_types;
        })
      );
  }

  newDocumentTypeSet() {
    this.selectedDocumentType = null;
    this.editDocumentType = null;
    this.addChildDocumentType = null;
    this.newDocumentType = new DocumentType();
  }

  nextTerm() {
    this.searchTermChanged.next(this.params.term);
  }

  reloadDocumentType(documentType: DocumentType) {
    this.updatedDocumentTypeNumber += 1;
    let documentTypeId: number = this.selectedDocumentType ? this.selectedDocumentType.id : this.newParentId;
    for (let documentType of this.documentTypes)
      if (documentType.id == documentTypeId)
        this.documentTypeService.get(documentType.id).then((response_json) => {
          documentType.prefix = response_json.document_type.prefix;
          documentType.name = response_json.document_type.name;
          documentType.prefix_letter = response_json.document_type.prefix_letter;
          documentType.description = response_json.document_type.description;
          documentType.is_active = response_json.document_type.is_active;
          documentType.children = response_json.document_type.children;
        });
  }

  toggleDocumentType(documentType: DocumentType) {
    documentType.expanded = !documentType.expanded;
    if (documentType.expanded) {
      documentType.loading = true;
      this.documentTypeService
        .getChildren(documentType.id)
        .then((response_json) => {
          documentType.loading = false;
          documentType.children = response_json.children;
        })
        .catch(() => {
          documentType.loading = false;
        });
    }
  }

  saveDocumentType(documentType: DocumentType) {
    let errors: Array<string> = [];
    if (!documentType.prefix) errors.push('Prefix is required.');
    if (!documentType.name) errors.push('Name is required.');
    if (errors.length > 0) {
      this.notyService.notify(errors.join(' '), 'error');
      return;
    }
    this.saving = true;
    this.documentTypeService
      .save(documentType)
      .then((response_json) => {
        this.saving = false;
        if (response_json.success) {
          this.notyService.notify(response_json.text || 'Document Type saved', 'success');
          if (this.newDocumentType && response_json.parent_id) this.newParentId = response_json.parent_id;
          else if (response_json.reload_document_types) this.loadDocumentTypes();
          this.reloadDocumentType(documentType);
          this.newDocumentType = null;
          this.editDocumentType = null;
          this.addChildDocumentType = null;
        } else this.notyService.notify(response_json.text || 'Error saving document type', 'error');
      })
      .catch(() => {
        this.notyService.notify('Error saving document type', 'error');
      });
  }

  selectDocumentType(documentType: DocumentType) {
    this.selectedDocumentType = documentType;
    this.addChildDocumentType = null;
    this.editDocumentType = null;
    this.newDocumentType = null;
  }

  setRightMarginTop() {
    if (!document.getElementById('right-card')) return;
    let topPosition: number = document.getElementById('right-card')!.parentElement!.getBoundingClientRect().top;
    if (topPosition >= 10) this.rightMarginTop = 0;
    else this.rightMarginTop = -topPosition + 10;
  }
}
